/* eslint-disable indent */
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CategorizationInput from '@src/main/modules/other_incidents/ui/components/CategorizationInput';
import FeedbackBanner from '@src/main/modules/other_incidents/ui/components/FeedbackBanner';
import FieldRow from '@src/main/modules/other_incidents/ui/components/FieldRow';
import { IncidentActionsSection } from '@src/main/modules/other_incidents/ui/components/IncidentActionsSection';
import { IncidentEmailSection } from '@src/main/modules/other_incidents/ui/components/IncidentEmailSection';
import { useIncidentActions } from '@src/main/modules/other_incidents/ui/hooks/useIncidentActions';
import { useDownloadImage } from '@src/main/shared/ui/hooks/usedownloadImage';
import { useGetUrlFile } from '@src/main/shared/ui/hooks/useGetUrlFile';
import { toLocalDateTimeWithSeconds } from '@src/main/shared/utils/dateUtils';
import { Incident } from '../../domain/incident.model';

type Props = {
  onHide: () => void;
  incident: Incident | null;
};

const IncidentDetailDialog = ({ onHide, incident }: Props) => {
  const { t } = useTranslation();
  const { mutate: downloadFile } = useDownloadImage();
  const { data: imageSrc } = useGetUrlFile(incident?.incidentImageKey, incident?.incidentImage);
  const [plainText, setPlainText] = useState<string>('');
  const [localIncident, setLocalIncident] = useState(incident);
  const [categorization, setCategorization] = useState<string>('');
  useEffect(() => {
    setLocalIncident(incident);
    setCategorization(incident?.categorization ?? '');
  }, [incident]);

  const isSentToColumbus = Boolean(
    localIncident?.linkColumbus || localIncident?.incidentColumbusNumber,
  );
  const [feedback, setFeedback] = useState<{
    message: string;
    type: 'success' | 'error' | null;
  }>({
    message: '',
    type: null,
  });
  const {
    emailText,
    setEmailText,
    isSending,
    sendEmail,
    sendToColumbus,
    uploadKey,
    updateCategorizacion,
  } = useIncidentActions(incident, setFeedback, (updated) =>
    setLocalIncident({ ...updated } as Incident),
  );
  const [file, setFile] = useState<File | null>(null);

  const download = useCallback(() => {
    if (!incident?.incidentImageKey || !incident?.incidentImage) return;

    downloadFile({
      keyFile: incident.incidentImageKey,
      fileName: incident.incidentImage,
    });
  }, [incident, downloadFile]);

  if (!localIncident || !incident) return null;

  return (
    <Dialog
      key={incident?.id}
      header={
        t('otherIncidentManagement.incidentDetail.title') +
        (localIncident.incidentColumbusNumber ? ` : ${localIncident.incidentColumbusNumber}` : '')
      }
      visible={!!incident}
      style={{ width: '100%', height: '90%' }}
      onHide={onHide}
    >
      <FeedbackBanner
        feedback={feedback}
        onClose={() => setFeedback({ message: '', type: null })}
      />
      <div style={{ display: 'flex', gap: '2rem', height: '100%' }}>
        {/* IZQUIERDA */}
        <div
          className="p-fluid"
          style={{ flex: incident.incidentImage ? 2 : 1, marginBottom: '1rem', overflowY: 'auto' }}
        >
          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.incidentDate')}
            value={toLocalDateTimeWithSeconds(new Date(incident.incidentDate))}
          />
          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.flightDate')}
            value={toLocalDateTimeWithSeconds(new Date(incident.flightDate))}
          />
          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.flightNumber')}
            value={`${incident.flightNumber} | ${incident.departure} 
        | ${incident.arrival} | ${incident.flightMat} | ${incident.flightFleet}`}
          />

          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.employeeId')}
            value={`${incident.employeeId} | ${incident.employeeName} (${incident.employeeRole})`}
          />

          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.appVersion')}
            value={`${incident.appVersion} | ${incident.iosVersion} `}
          />

          <FieldRow
            label={t('otherIncidentManagement.incidentDetail.description')}
            value={incident.comment}
          />

          <CategorizationInput
            value={categorization}
            onChange={setCategorization}
            onAdd={async () => {
              await updateCategorizacion(categorization);
              setLocalIncident((prev) => (prev ? { ...prev, categorization } : prev));
            }}
            t={t}
          />

          <IncidentEmailSection
            emailText={emailText}
            setEmailText={setEmailText}
            setPlainText={setPlainText}
            setFile={setFile}
            t={t}
            uploadKey={uploadKey}
          />
          <IncidentActionsSection
            isSentToColumbus={isSentToColumbus}
            isSending={isSending}
            sendToColumbus={sendToColumbus}
            sendEmail={sendEmail}
            plainText={plainText}
            file={file}
            t={t}
            localIncident={localIncident}
          />
        </div>

        {/* DERECHA */}
        <div
          style={{
            flex: 1,
            display: incident.incidentImage ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {imageSrc && (
            <img
              src={imageSrc}
              alt="Incident"
              style={{
                width: '100%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          )}

          {incident.incidentImageKey && incident.incidentImage && (
            <Button
              label={t('otherIncidentManagement.incidentDetail.download')}
              icon="pi pi-download"
              className="p-button-secondary"
              onClick={download}
              style={{ marginBottom: '1rem', marginTop: '1rem' }}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default IncidentDetailDialog;
